/** «Аналитика МП» (`/ozon-analytics`, право `ozon_analytics`): фин. результат площадки (Ozon |
 *  WB, `mp=wb` в адресе) за любой период со сравнением — сводка P&L, дерево детализации, тренд,
 *  поставки (Ozon), сверка; «По дням» — месяц по дням с итогами недель (свой месяц, без периода
 *  шапки); вкладка «План» — план/факт месяца по обеим площадкам сразу. Различия площадок — только mp.ts. Всё состояние — в адресе
 *  (state.ts). Источник правды — docs/ozon-analytics.md. */
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { MartState, OzFilters } from "@/api/ozonAnalytics";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { cn } from "@/lib/utils";
import { fmtBuiltMsk, fmtDate, martStaleWarning, timeAgo } from "./format";
import { cmpOutOfData, cmpWarningText } from "./cmpCheck";
import { CogsCoverageChip } from "./CogsCoverage";
import { CMP_MODES, monthsOf, PRESETS, todayIso, type CmpMode, type Range } from "./period";
import { dailyLegals, legalOptions, legalsWithData } from "./legals";
import { MP_CONFIGS, MP_OPTIONS, MpContext, useMp, type Mp } from "./mp";
import { metaQuery, useDaily, useMeta, useMpRecon, type MpReconQuery } from "./queries";
import { ReconTab } from "./ReconTab";
import { ErrorBox, Loading, Seg } from "./shared";
import { SourcesFreshness } from "./SourcesFreshness";
import { EntityPanel } from "./EntityPanel";
import {
  applyPatch,
  CARD_BACK_STATE,
  cardPatch,
  channelPatch,
  cmpPatch,
  customPeriodPatch,
  forecastPatch,
  levelsPatch,
  mpParam,
  monthPatch,
  mpPatch,
  presetPatch,
  readState,
  tabsOf,
  toFilters,
  treePresetPatch,
  type CardRef,
  type Patch,
} from "./state";
import { DailyTab } from "./DailyTab";
import { SuppliesTab } from "./SuppliesTab";
import { PlanTab } from "./PlanTab";
import { SummaryTab } from "./SummaryTab";
import { TreeTab } from "./TreeTab";
import { trustStatus, wbTrustStatus, type Trust } from "./trust";
import { apiErrorText, selectClass } from "./ui";
import { WbReconTab } from "./WbReconTab";

/** Плашка доверия по сверке своей площадки. */
function reconTrust(recon: MpReconQuery, months: string[], legals: string[]): Trust | null {
  if (recon.mp === "ozon") return recon.q.data ? trustStatus(recon.q.data, months, legals) : null;
  return recon.q.data ? wbTrustStatus(recon.q.data, months, legals) : null;
}

/** Диапазон дат с черновиком: календарь отдаёт сначала одну дату, применяем, когда есть обе. */
function RangePicker({
  value,
  onApply,
  placeholder,
}: {
  value: Range | null;
  onApply: (r: Range) => void;
  placeholder: string;
}): React.ReactElement {
  const [draft, setDraft] = useState<{ from: string; to: string }>({
    from: value?.from ?? "",
    to: value?.to ?? "",
  });
  useEffect(() => setDraft({ from: value?.from ?? "", to: value?.to ?? "" }), [value?.from, value?.to]);
  return (
    <DateRangeFilter
      dateFrom={draft.from}
      dateTo={draft.to}
      placeholder={placeholder}
      size="sm"
      resetOnSelect
      onChange={(from, to) => {
        setDraft({ from, to });
        if (from && to && (from !== value?.from || to !== value?.to)) onApply({ from, to });
      }}
    />
  );
}

/** Страница: неизвестная площадка в адресе — ошибка без запросов (не молча Ozon). */
export function OzonAnalyticsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const mp = mpParam(params);
  if (mp.mp === null) {
    return (
      <div className="mx-auto max-w-none space-y-4">
        <h1 className="text-2xl font-bold">Аналитика МП</h1>
        <div
          role="alert"
          data-testid="unknown-mp"
          className="flex flex-wrap items-center gap-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          <span>
            Неизвестная площадка «{mp.unknown}» в адресе (есть: {MP_OPTIONS.map((o) => o.label).join(", ")}).
          </span>
          <button
            type="button"
            className="rounded border border-red-300 bg-background px-2 py-0.5 text-foreground hover:bg-accent dark:border-red-900"
            onClick={() => setParams((prev) => applyPatch(prev, { mp: null }), { replace: true })}
          >
            Открыть Ozon
          </button>
        </div>
      </div>
    );
  }
  return <AnalyticsPage mp={mp.mp} />;
}

function AnalyticsPage({ mp }: { mp: Mp }): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const today = todayIso();
  const cfg = MP_CONFIGS[mp];
  // вкладка «План» — обе площадки, без периода и юрлица: справочник и сверка площадки ей не нужны
  // (вкладка plan есть у всех площадок, поэтому сырой параметр = разобранный readState)
  const isPlan = params.get("tab") === "plan";
  // вкладка «По дням» — площадка и юрлицо шапки, но свой месяц: период, сравнение, общие расходы,
  // плашки периода (свежесть, сверка, покрытие) и сверка площадки ей не нужны (свежесть — своя)
  const isDaily = params.get("tab") === "daily";
  const usesPeriod = !isPlan && !isDaily;
  const meta = useMeta(mp, !isPlan);
  // конец данных витрины нужен при расчёте периода сравнения (обрезка текущего периода)
  const dataTo = meta.data?.mart.date_to ?? null;
  const state = useMemo(() => readState(params, today, dataTo), [params, today, dataTo]);
  const filters = useMemo(() => toFilters(state), [state]);

  const patch = useCallback(
    (p: Patch) => setParams((prev) => applyPatch(prev, p), { replace: true }),
    [setParams],
  );

  // открытие карточки — новая запись истории: «Назад» в браузере закрывает панель;
  // закрытие (✕, Esc, подложка) заменяет запись карточки, не добавляя лишнюю
  const openCard = useCallback(
    (c: CardRef) => setParams((prev) => applyPatch(prev, cardPatch(c)), { replace: false }),
    [setParams],
  );
  // переход из карточки в карточку (артикул → поставка) — тоже новая запись; с пометкой в
  // state, чтобы панель показала «Назад» к предыдущей карточке
  const openCardFromCard = useCallback(
    (c: CardRef) =>
      setParams((prev) => applyPatch(prev, cardPatch(c)), {
        replace: false,
        state: { [CARD_BACK_STATE]: true },
      }),
    [setParams],
  );
  const closeCard = useCallback(() => patch(cardPatch(null)), [patch]);

  // смена площадки: юрлицо проверяем по /meta НОВОЙ площадки (из кэша или запросом). /meta не
  // ответила — юрлицо в адресе не трогаем (проверить не по чему, `legalNames = null`); ошибку
  // /meta страница новой площадки покажет сама (тот же запрос в кэше в состоянии ошибки).
  // Пока /meta новой площадки едет, `switching` = она: кнопка площадки уже нажата, вместо данных
  // старой площадки — «Загружаем WB…» (данные Ozon под кнопкой WB выглядели бы текущими).
  // Последний клик побеждает: ответ /meta отменённого переключения адрес не трогает.
  const qc = useQueryClient();
  const [switching, setSwitching] = useState<Mp | null>(null);
  const switchSeq = useRef(0);
  useEffect(
    () => () => {
      switchSeq.current += 1; // ушли со страницы — недоехавшее переключение адрес не меняет
    },
    [],
  );
  const switchMp = useCallback(
    async (next: Mp) => {
      const seq = ++switchSeq.current;
      setSwitching(next);
      let legalNames: string[] | null;
      try {
        legalNames = (await qc.fetchQuery(metaQuery(next))).legal_names;
      } catch (err) {
        console.error(`/meta площадки ${next} не ответила — юрлицо не проверено`, err);
        legalNames = null;
      }
      if (seq !== switchSeq.current) return;
      setParams((prev) => applyPatch(prev, mpPatch(readState(prev, today), next, legalNames)), {
        replace: true,
      });
      setSwitching(null);
    },
    [qc, setParams, today],
  );
  const onMpClick = (v: Mp): void => {
    if (v === (switching ?? mp)) return;
    if (v === mp) {
      // передумали во время загрузки — остаёмся на текущей площадке
      switchSeq.current += 1;
      setSwitching(null);
      return;
    }
    void switchMp(v);
  };

  // Период без дат в адресе (первый заход) — дописываем пресет и его даты: ссылка стабильна во
  // времени, а p без дат и даты без p readState читает по-разному
  useEffect(() => {
    if (usesPeriod && (!params.get("from") || !params.get("to"))) {
      patch({ p: state.preset, from: state.period.from, to: state.period.to });
    }
  }, [usesPeriod, params, patch, state.preset, state.period.from, state.period.to]);

  const months = monthsOf(state.period);
  const recon = useMpRecon(mp, months[0]!, months[months.length - 1]!, state.legal, usesPeriod);

  // юрлица переключателя — с данными в периоде шапки; на «По дням» — витрины финансов ∪ витрины
  // заказов (тот же запрос, что у вкладки — из кэша), без отбора по датам фактов финансов
  const daily = useDaily(mp, state.month, state.legal, state.channel, isDaily);
  const ordersLegals = useMemo(
    () => (daily.data ? Object.keys(daily.data.orders.data_through) : null),
    [daily.data],
  );
  const activeLegals = useMemo(() => {
    if (!meta.data) return null;
    if (isDaily) return dailyLegals(meta.data.legal_names, ordersLegals, state.legal);
    return legalsWithData(meta.data.legal_names, meta.data.legal_ranges, state.period, state.cmp);
  }, [meta.data, isDaily, ordersLegals, state.legal, state.period, state.cmp]);
  const legalsForTrust = state.legal ? [state.legal] : activeLegals;
  const cmpWarning = meta.data
    ? cmpWarningText(
        cmpOutOfData(state.cmp, meta.data.legal_ranges, activeLegals ?? [], state.legal),
      )
    : null;

  const legalButtons = legalOptions(activeLegals ?? [], state.legal);

  return (
    <MpContext.Provider value={cfg}>
    <div className="mx-auto max-w-none space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Аналитика МП</h1>
        {!isPlan && (
          <Seg label="Площадка" value={switching ?? mp} options={MP_OPTIONS} onChange={onMpClick} />
        )}
        {!isPlan && !switching && cfg.vatNote && (
          <span className="text-xs text-muted-foreground" data-testid="vat-note">
            {cfg.vatNote}
          </span>
        )}
      </div>

      {/* Период и сравнение */}
      {usesPeriod && (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Seg
          label="Период"
          value={state.preset}
          options={PRESETS.filter((p) => p.id !== "custom")}
          onChange={(id) => {
            if (id !== "custom") patch(presetPatch(id, today));
          }}
        />
        <RangePicker
          value={state.period}
          placeholder="Свой период"
          onApply={(r) => patch(customPeriodPatch(r))}
        />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Сравнить с
          <select
            className={selectClass}
            value={state.cmpMode}
            aria-label="Сравнение"
            onChange={(e) => patch(cmpPatch(e.target.value as CmpMode, state.cmp))}
          >
            {CMP_MODES.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        {state.cmpMode === "custom" && (
          <RangePicker
            value={state.cmp}
            placeholder="Выберите период сравнения"
            onApply={(r) => patch(cmpPatch("custom", r))}
          />
        )}
        {state.cmp && state.cmpMode !== "custom" && (
          <span className="text-xs text-muted-foreground" data-testid="cmp-range">
            {fmtDate(state.cmp.from)} — {fmtDate(state.cmp.to)}
          </span>
        )}
        {state.cmpMode === "custom" && !state.cmp && (
          <span className="text-xs text-amber-700 dark:text-amber-400">
            период сравнения не выбран — сравнение выключено
          </span>
        )}
      </div>
      )}

      {switching ? (
        <div data-testid="mp-switching" aria-busy="true">
          <Loading text={`Загружаем ${MP_CONFIGS[switching].label}…`} />
        </div>
      ) : (
      <>
      {!isPlan && (
      <>
      {/* Юрлицо, общие расходы */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {meta.data ? (
          <Seg
            label="Юрлицо"
            value={state.legal ?? ""}
            options={legalButtons}
            onChange={(v) => patch({ legal: v || null })}
          />
        ) : (
          state.legal && <span className="text-sm">Юрлицо: {state.legal}</span>
        )}
        {usesPeriod && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={state.alloc}
              onChange={(e) => patch({ alloc: e.target.checked ? "1" : null })}
            />
            Общие расходы по выручке
          </label>
        )}
      </div>

      {meta.isError && <ErrorBox error={meta.error} what="справочник витрины (/meta)" />}
      {usesPeriod && (
      <StatusLine
        mart={meta.data?.mart ?? null}
        period={state.period}
        trust={legalsForTrust ? reconTrust(recon, months, legalsForTrust) : null}
        cmpWarning={cmpWarning}
        reconError={recon.q.isError ? apiErrorText(recon.q.error, cfg.label) : null}
        filters={filters}
        coverage={usesPeriod}
      />
      )}
      </>
      )}

      <div className="flex gap-4 border-b">
        {tabsOf(mp).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={state.tab === t.id}
            onClick={() => patch({ tab: t.id === "summary" ? null : t.id })}
            className={cn(
              "-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors",
              state.tab === t.id
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {meta.isLoading && <Loading text="Загружаю справочник витрины…" />}
      {/* key: другая площадка — вкладки с нуля (раскрытия дерева, черновик поиска и т.п.) */}
      <React.Fragment key={mp}>
      {state.tab === "plan" && (
        <PlanTab
          month={state.month}
          today={today}
          onMonth={(m) => patch(monthPatch(m, today))}
          forecastMode={state.forecast}
          onForecastMode={(f) => patch(forecastPatch(f))}
        />
      )}
      {!meta.isLoading && state.tab === "summary" && (
        <SummaryTab
          cmpWarning={cmpWarning}
          filters={filters}
          grain={state.grain}
          onGrain={(g) => patch({ grain: g === "month" ? null : g })}
        />
      )}
      {state.tab === "daily" && (
        <DailyTab
          month={state.month}
          today={today}
          legal={state.legal}
          channel={state.channel}
          onMonth={(m) => patch(monthPatch(m, today))}
          onChannel={(c) => patch(channelPatch(c))}
        />
      )}
      {!meta.isLoading && state.tab === "tree" && (
        <TreeTab
          state={state}
          filters={filters}
          dims={meta.data?.dims ?? null}
          onLevels={(lv) => patch(levelsPatch(lv, mp))}
          onPreset={(id) => patch(treePresetPatch(id, mp))}
          onPatch={patch}
          onCard={openCard}
        />
      )}
      {!meta.isLoading && state.tab === "supplies" && (
        <SuppliesTab
          filters={filters}
          by={state.supplyBy}
          onlyIssues={state.onlyIssues}
          onPatch={patch}
          onCard={openCard}
        />
      )}
      {state.tab === "recon" &&
        (recon.mp === "ozon" ? <ReconTab query={recon.q} /> : <WbReconTab query={recon.q} />)}
      </React.Fragment>
      </>
      )}
    </div>
    {/* карточка сущности — поверх любой вкладки; адрес `card` (ссылкой можно поделиться).
        Вне контейнера `space-y-4`: иначе панель получает margin-top и шапка не затемнена */}
    {!isPlan && !isDaily && !switching && !meta.isLoading && state.card && (
      <EntityPanel
        card={state.card}
        filters={filters}
        cmpWarning={cmpWarning}
        onClose={closeCard}
        onOpenCard={openCardFromCard}
      />
    )}
    </MpContext.Provider>
  );
}

function StatusLine({
  mart,
  period,
  trust,
  reconError,
  cmpWarning,
  filters,
  coverage,
}: {
  mart: MartState | null;
  period: Range;
  trust: ReturnType<typeof trustStatus> | null;
  reconError: string | null;
  cmpWarning: string | null;
  /** фильтры страницы — для плашки покрытия себестоимости (период, юрлицо) */
  filters: OzFilters;
  /** запрашивать покрытие себестоимости (false — вкладка «План») */
  coverage: boolean;
}): React.ReactElement {
  const mp = useMp();
  const beyond = mart && period.to > mart.date_to;
  const before = mart && period.from < mart.date_from;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {mart && (
        <span className="rounded-full border px-2 py-0.5 text-muted-foreground" data-testid="freshness">
          Витрина собрана {fmtBuiltMsk(mart.built_at)} ({timeAgo(mart.built_at)}) · данные {fmtDate(mart.date_from)} —{" "}
          {fmtDate(mart.date_to)}
        </span>
      )}
      {mart && martStaleWarning(mart.built_at) && (
        <span
          role="alert"
          data-testid="mart-stale"
          className="rounded-md border border-amber-500/50 bg-amber-50 dark:border-amber-700/60 px-2 py-0.5 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
        >
          {martStaleWarning(mart.built_at)}
        </span>
      )}
      {mart && mp.freshnessBySource && (mart.data_through ? (
        <SourcesFreshness dataThrough={mart.data_through} />
      ) : (
        <span
          data-testid="sources-freshness-missing"
          className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
        >
          витрина не отдала свежесть по источникам
        </span>
      ))}
      {beyond && (
        <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          период длиннее витрины: данные только по {fmtDate(mart.date_to)}
        </span>
      )}
      {before && (
        <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          данные витрины начинаются с {fmtDate(mart.date_from)}
        </span>
      )}
      {cmpWarning && (
        <span
          data-testid="cmp-out-of-data"
          className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
        >
          {cmpWarning}
        </span>
      )}
      {trust && (
        <span
          data-testid="trust"
          className={cn(
            "rounded-full border px-2 py-0.5",
            trust.kind === "ok" &&
              "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
            trust.kind === "mismatch" &&
              "border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400",
            trust.kind === "missing" &&
              "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
          )}
        >
          {trust.kind === "ok" ? "✓ " : trust.kind === "mismatch" ? "✗ " : ""}
          {trust.text}
        </span>
      )}
      {reconError && (
        <span className="rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
          сверка недоступна: {reconError}
        </span>
      )}
      <CogsCoverageChip filters={filters} enabled={coverage} />
    </div>
  );
}
